import { useEffect, useState } from 'react';

import { LaneType } from 'common-types';
import { getClient } from '../apollo';
import { getContentFeatureReservableAvailability } from '../graphql/content';
import { useReservableHeadcountCapacityEnabled } from './useReservableHeadcountCapacityEnabled';
import DateRange from '../properties/baseTypes/DateRange';
import { DateRangeType } from '../types/baseTypes/DateRangeType';
import { getContentFeatureReservableRemainingCapacity } from 'graphql-queries';
import { ReservableAvailabilityReason } from 'domains-reservable-availability';

export enum ReservableEventTypeEnum {
  Buffer = 'Buffer',
  Reservation = 'Reservation',
  TimeUnavailable = 'TimeUnavailable',
}

/**
 * Kept as an alias so existing importers keep working. The canonical enum lives
 * in domains-reservable-availability, which also produces these values.
 */
export { ReservableAvailabilityReason as ReservableAvailabilityReasonEnum };

export type ReservableUnavailabilityRange = {
  interval: DateRangeType;
  eventType: ReservableEventTypeEnum | null;
};

export type ReservableAvailabilityRange = ReservableUnavailabilityRange & {
  remainingSpots: number | null;
  reason: ReservableAvailabilityReason | null;
};

type Props = {
  contentId?: LaneType.UUID;
  startDate?: Date | null;
  endDate?: Date | null;
};

export default function useReservableAvailabilityByRange({
  contentId,
  startDate,
  endDate,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [unavailableDateRanges, setUnavailableDateRanges] = useState<
    ReservableUnavailabilityRange[]
  >([]);
  const [availabilityRanges, setAvailabilityRanges] = useState<
    ReservableAvailabilityRange[]
  >([]);

  const isHeadcountCapacityEnabled = useReservableHeadcountCapacityEnabled();

  async function getReservableAvailability() {
    if (!startDate || !endDate) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const availabilityPromise = getClient().query<{
        contentFeatureReservableAvailability: {
          startDate: string;
          endDate: string;
          eventType: ReservableEventTypeEnum | null;
        }[];
      }>({
        query: getContentFeatureReservableAvailability,
        fetchPolicy: 'network-only',
        variables: {
          id: contentId,
          startDate,
          endDate,
        },
      });

      const capacityPromise = isHeadcountCapacityEnabled
        ? getClient()
            .query<{
              contentFeatureReservableRemainingCapacity: {
                startDate: string;
                endDate: string;
                eventType: ReservableEventTypeEnum | null;
                remainingSpots: number | null;
                reason: ReservableAvailabilityReason | null;
              }[];
            }>({
              query: getContentFeatureReservableRemainingCapacity,
              fetchPolicy: 'network-only',
              variables: {
                id: contentId,
                startDate,
                endDate,
              },
            })
            .catch((err: Error) => ({ _capacityError: err }))
        : null;

      const [result, capacityResult] = await Promise.all([
        availabilityPromise,
        capacityPromise,
      ]);

      setUnavailableDateRanges(
        result.data.contentFeatureReservableAvailability.map(
          ({ startDate, endDate, eventType }) => ({
            interval: DateRange.parseValue({ startDate, endDate }),
            eventType,
          })
        )
      );

      if (capacityResult && '_capacityError' in capacityResult) {
        setError(capacityResult._capacityError);
        setAvailabilityRanges([]);
      } else if (capacityResult) {
        setAvailabilityRanges(
          capacityResult.data.contentFeatureReservableRemainingCapacity.map(
            ({ startDate, endDate, eventType, remainingSpots, reason }) => ({
              interval: DateRange.parseValue({ startDate, endDate }),
              eventType,
              remainingSpots,
              reason,
            })
          )
        );
      }
    } catch (err) {
      setError(err);
      setAvailabilityRanges([]);
    }

    setLoading(false);
  }

  useEffect(() => {
    if (!contentId || !startDate || !endDate || loading) {
      return;
    }

    getReservableAvailability();
  }, [
    contentId,
    startDate?.toString(),
    endDate?.toString(),
    isHeadcountCapacityEnabled,
  ]);

  return {
    unavailableDateRanges,
    availabilityRanges,
    loading,
    error,
    getReservableAvailability,
  };
}
